import { useState } from 'react';
import { useMessages } from './i18n/context';
import type { ChildListingResponse } from './api/types';

/**
 * The sub-planlets under the open plan (ADR-027 §1 — resources nest) with the caller's
 * effective access to each, and the create gesture for callers who may write the parent.
 */
export function ChildPlans({
  items,
  busy,
  canCreate,
  onOpen,
  onCreate,
}: {
  /** The children listing, or null while it loads. */
  items: readonly ChildListingResponse[] | null;
  busy: boolean;
  canCreate: boolean;
  onOpen: (planId: string, name: string | null) => void;
  onCreate: (name: string) => void;
}) {
  const t = useMessages();
  const g = t.grove;
  const [name, setName] = useState('');
  const trimmed = name.trim();

  return (
    <section className="subplans" aria-labelledby="sub-planlets">
      <div className="section-heading">
        <h3 id="sub-planlets">{t.childPlans.heading}</h3>
        <p>{g.subplanDescription}</p>
      </div>
      {items === null && <p className="empty-state">{t.childPlans.loading}</p>}
      {items !== null && items.length === 0 && <p className="empty-state">{t.childPlans.empty}</p>}
      {items !== null && items.length > 0 && (
        <ul className="subplan-grid">
          {items.map((child) => (
            <li className="subplan-card card" key={child.id}>
              <button className="subplan-title" disabled={busy} onClick={() => onOpen(child.id, child.name)}>
                {child.name}
                <span aria-hidden="true">↗</span>
              </button>
              <div className="subplan-meta">
                <span className="access-badge" data-access={child.access} aria-label={t.childPlans.accessTo(child.name)}>
                  {t.access[child.access]}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {canCreate && (
        <form
          className="add-form"
          aria-label={t.childPlans.add}
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed !== '') {
              onCreate(trimmed);
              setName('');
            }
          }}
        >
          <label className="field">
            <span>{t.childPlans.nameLabel}</span>
            <input
              aria-label={t.childPlans.nameLabel}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={g.subplanPlaceholder}
              disabled={busy}
            />
          </label>
          <button className="button-secondary" type="submit" disabled={busy || trimmed === ''}>
            {t.childPlans.add}
          </button>
        </form>
      )}
    </section>
  );
}
